import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Icono, IconBubble } from "./iconos";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const VERDE   = "#7BC74D";
const VIOLETA = "#7c3aed";
const CYAN    = "#06b6d4";

const ESTADOS = {
    nuevo:          { label: "Nuevos",         color: "#1877F2" },
    contactado:     { label: "Contactados",    color: "#f0a500" },
    en_seguimiento: { label: "En seguimiento", color: "#17a2b8" },
    cerrado:        { label: "Cerrados",       color: VERDE },
    sin_interes:    { label: "Sin interés",    color: "#6c757d" },
};

const fmtNum = (n, dec = 0) =>
    n == null ? null : Number(n).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtPesos = (n) => n == null ? null : `US$ ${fmtNum(n, 2)}`;

function PctBadge({ pct }) {
    if (pct == null) return <span style={{ fontSize: "0.72rem", color: "#bbb" }}>—</span>;
    const up = pct >= 0;
    return (
        <span style={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color: up ? "#3a7d1e" : "#c0392b",
        }}>
            {up ? "+" : ""}{pct}%
            <span style={{ color: "#aaa", fontWeight: 400, marginLeft: 4 }}>vs período anterior</span>
        </span>
    );
}

function KpiCard({ icono, label, value, pct, color, sinDatos }) {
    const c = color || VERDE;
    return (
        <div className="col-6 col-md-4 col-lg-2 mb-3">
            <div className="card h-100 border-0 shadow-sm">
                <div className="card-body py-3 px-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <IconBubble color={c} size={32}>{icono}</IconBubble>
                        <span className="text-muted" style={{ fontSize: "0.78rem", fontWeight: 500 }}>
                            {label}
                        </span>
                    </div>
                    <div style={{
                        fontSize: "1.7rem",
                        fontWeight: 700,
                        color: "#222",
                        lineHeight: 1.1,
                        marginBottom: 4,
                    }}>
                        {value ?? (sinDatos
                            ? <span style={{ fontSize: "0.85rem", color: "#bbb", fontWeight: 400 }}>sin datos</span>
                            : <span style={{ color: "#ccc" }}>—</span>
                        )}
                    </div>
                    <PctBadge pct={pct} />
                </div>
            </div>
        </div>
    );
}

export default function ZTResumen() {
    const { darkMode } = useContext(DarkModeContext);
    const navegacion = useNavigate();
    const [autorizado, setAutorizado] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [datos, setDatos] = useState(null);

    useEffect(() => {
        const init = async () => {
            const token = getToken();
            if (!token) { navegacion("/"); return; }
            try {
                const r = await fetch(process.env.REACT_APP_API_ZT_RESUMEN, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Token: token }),
                });
                if (r.status === 401 || r.status === 403) { navegacion("/"); return; }
                if (r.ok) {
                    setDatos(await r.json());
                    setAutorizado(true);
                }
            } catch { navegacion("/"); }
            finally { setCargando(false); }
        };
        init();
    }, [navegacion]);

    if (cargando) return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <div className="spinner-border" style={{ color: VERDE }} />
        </div>
    );
    if (!autorizado || !datos) return null;

    const metaOk = datos.metaDisponible;

    // Gráfico tendencia: línea Leads + línea CPL
    const allDates = new Set([
        ...(datos.leadsDiarios || []).map(d => d.fecha),
        ...(datos.spendDiario  || []).map(d => d.fecha),
    ]);
    const trendLabels = [...allDates].sort();
    const leadsMap = Object.fromEntries((datos.leadsDiarios || []).map(d => [d.fecha, d.leads]));
    const spendMap = Object.fromEntries((datos.spendDiario  || []).map(d => [d.fecha, d.spend]));

    // CPL diario = spend del día / leads del día
    const cplDiario = trendLabels.map(f => {
        const l = leadsMap[f] || 0;
        const s = spendMap[f] || 0;
        return l > 0 ? s / l : null;
    });

    const trendData = {
        labels: trendLabels,
        datasets: [
            {
                label: "Leads",
                data: trendLabels.map(f => leadsMap[f] ?? 0),
                borderColor: VIOLETA,
                backgroundColor: "rgba(124,58,237,0.08)",
                tension: 0.35,
                pointRadius: 4,
                pointBackgroundColor: VIOLETA,
                pointBorderColor: "#fff",
                pointBorderWidth: 2,
                yAxisID: "y",
            },
            ...(metaOk ? [{
                label: "Costo por lead (CPL)",
                data: cplDiario,
                borderColor: CYAN,
                backgroundColor: "rgba(6,182,212,0.08)",
                tension: 0.35,
                pointRadius: 4,
                pointBackgroundColor: CYAN,
                pointBorderColor: "#fff",
                pointBorderWidth: 2,
                yAxisID: "y1",
                spanGaps: true,
            }] : []),
        ],
    };

    // Embudo de conversión (con porcentajes basados en total de leads recibidos)
    const total30 = datos.totalLeads30 || 1;
    const recibidos      = datos.totalLeads30 || 0;
    const contactados    = (datos.conteoEstados?.contactado    || 0) + (datos.conteoEstados?.en_seguimiento || 0) + (datos.conteoEstados?.cerrado || 0);
    const calificados    = (datos.conteoEstados?.en_seguimiento || 0) + (datos.conteoEstados?.cerrado || 0);
    const demoAgendada   = datos.conteoEstados?.en_seguimiento || 0;
    const activados      = datos.conteoEstados?.cerrado || 0;

    const embudo = [
        { label: "Leads recibidos", count: recibidos,   color: VERDE,    pct: 100 },
        { label: "Contactados",     count: contactados, color: "#a8d870", pct: total30 ? Math.round(contactados / total30 * 100) : 0 },
        { label: "Calificados",     count: calificados, color: "#bbe07f", pct: total30 ? Math.round(calificados / total30 * 100) : 0 },
        { label: "Demo agendada",   count: demoAgendada, color: "#cee990", pct: total30 ? Math.round(demoAgendada / total30 * 100) : 0 },
        { label: "Activados",       count: activados,   color: "#dff2b3", pct: total30 ? Math.round(activados / total30 * 100) : 0 },
    ];
    const embudoMax = Math.max(...embudo.map(e => e.count), 1);

    // Insights
    const insights = [];
    if (datos.pctCpl != null && datos.pctCpl < 0)
        insights.push({ titulo: "El rendimiento está por encima del promedio", detalle: `Tu CPL es ${Math.abs(datos.pctCpl)}% más bajo que el promedio del período anterior.` });
    if (datos.pctLeads != null && datos.pctLeads > 0)
        insights.push({ titulo: "Tendencia de leads positiva", detalle: `Los leads aumentaron un ${datos.pctLeads}% vs el período anterior.` });
    if (calificados > 0)
        insights.push({ titulo: "Mejorá la segmentación", detalle: "Concentrá presupuesto en audiencias similares a quienes ya calificaron." });
    const nuevosSinContactar = datos.conteoEstados?.nuevo || 0;
    if (nuevosSinContactar > 0)
        insights.push({ titulo: "Agendá más demos", detalle: `Hay ${nuevosSinContactar} leads nuevos sin contactar — los contactados tienen 2,1× más chances de avanzar.` });

    return (
        <div className={darkMode ? "container-dark p-3" : "p-3"}>
            <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                <div>
                    <h4 className="fw-bold mb-1" style={{ color: "#222" }}>Resumen ejecutivo</h4>
                    <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                        Aquí tienes el rendimiento general de tus campañas.
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "0.78rem" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: VERDE }} />
                    Actualizado hace 5 min
                </div>
            </div>

            {/* KPIs */}
            <div className="row g-2 mb-3">
                <KpiCard icono={<Icono.Users />}        label="Leads"        value={fmtNum(datos.totalLeads30)} pct={datos.pctLeads}      color={VERDE} />
                <KpiCard icono={<Icono.Dollar />}       label="CPL"          value={fmtPesos(datos.cpl)}        pct={datos.pctCpl != null ? -datos.pctCpl : null} color={VERDE} sinDatos={!metaOk} />
                <KpiCard icono={<Icono.Wallet />}       label="Inversión"    value={fmtPesos(datos.gasto)}      pct={datos.pctGasto}       color={VERDE} sinDatos={!metaOk} />
                <KpiCard icono={<Icono.Target />}       label="Alcance"      value={fmtNum(datos.alcance)}      pct={datos.pctAlcance}     color={VERDE} sinDatos={!metaOk} />
                <KpiCard icono={<Icono.Eye />}          label="Impresiones"  value={fmtNum(datos.impresiones)}  pct={datos.pctImpresiones} color={VERDE} sinDatos={!metaOk} />
                <KpiCard icono={<Icono.MousePointer />} label="CTR"          value={datos.ctr != null ? `${fmtNum(datos.ctr, 2)}%` : null} pct={datos.pctCtr} color={VERDE} sinDatos={!metaOk} />
            </div>

            {/* Tendencia + Embudo */}
            <div className="row g-3 mb-3">
                <div className="col-12 col-lg-7">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0">Tendencia de leads y costo por resultado</h6>
                                <span className="text-muted" style={{ fontSize: "0.75rem", border: "1px solid #ddd", borderRadius: 6, padding: "3px 10px" }}>
                                    Diario
                                </span>
                            </div>
                            {trendLabels.length > 0 ? (
                                <Line
                                    data={trendData}
                                    options={{
                                        responsive: true,
                                        interaction: { mode: "index", intersect: false },
                                        plugins: {
                                            legend: {
                                                position: "top",
                                                align: "start",
                                                labels: {
                                                    boxWidth: 8, boxHeight: 8, usePointStyle: true,
                                                    pointStyle: "circle", font: { size: 11 },
                                                },
                                            },
                                        },
                                        scales: {
                                            y: {
                                                position: "left",
                                                beginAtZero: true,
                                                ticks: { precision: 0, color: VIOLETA, font: { size: 11 } },
                                                grid: { color: "rgba(0,0,0,0.05)" },
                                            },
                                            ...(metaOk ? {
                                                y1: {
                                                    position: "right",
                                                    beginAtZero: true,
                                                    ticks: {
                                                        color: CYAN, font: { size: 11 },
                                                        callback: (v) => `US$ ${v}`,
                                                    },
                                                    grid: { drawOnChartArea: false },
                                                },
                                            } : {}),
                                            x: { ticks: { font: { size: 11 } }, grid: { display: false } },
                                        },
                                    }}
                                />
                            ) : (
                                <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 200, color: "#bbb" }}>
                                    <span style={{ fontSize: "0.85rem" }}>Sin datos de tendencia</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-5">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="fw-bold mb-3">Embudo de conversión</h6>
                            {embudo.map((e, i) => (
                                <div key={i} className="mb-3 d-flex align-items-center gap-2">
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            backgroundColor: e.color,
                                            borderRadius: 6,
                                            padding: "10px 14px",
                                            width: `${Math.max(40, (e.count / embudoMax) * 100)}%`,
                                            minWidth: 140,
                                            color: "#222",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}>
                                            <span style={{ fontSize: "0.82rem", fontWeight: 500 }}>{e.label}</span>
                                            <strong style={{ fontSize: "0.95rem", marginLeft: 12 }}>{e.count}</strong>
                                        </div>
                                    </div>
                                    <span style={{
                                        fontSize: "0.85rem",
                                        fontWeight: 700,
                                        color: "#666",
                                        minWidth: 50,
                                        textAlign: "right",
                                    }}>
                                        {e.pct}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Últimos leads + Insights */}
            <div className="row g-3">
                <div className="col-12 col-lg-7">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                    <Icono.Users color="#666" /> Últimos leads
                                </h6>
                                <button className="btn btn-sm" style={{ fontSize: "0.75rem", color: VERDE, fontWeight: 600 }}>
                                    Ver todos los leads →
                                </button>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                    <thead>
                                        <tr style={{ borderBottom: "1px solid #eee" }}>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999", paddingBottom: 10 }}>Fecha</th>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }}>Nombre</th>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }}>Teléfono</th>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }}>Email</th>
                                            <th style={{ fontSize: "0.7rem", fontWeight: 500, color: "#999" }}>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(datos.ultimosLeads || []).slice(0, 5).map(l => {
                                            const fecha = new Date(l.fechaRecibido);
                                            const fechaStr = `${fecha.toLocaleDateString("es-AR")}, ${fecha.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}`;
                                            const iniciales = (l.nombre || "—").split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join(". ") + ".";
                                            const emailMask = l.email ? l.email[0] + "****@" + (l.email.split("@")[1] || "") : "—";
                                            const estado = ESTADOS[l.estado] || { label: l.estado, color: "#aaa" };
                                            return (
                                                <tr key={l.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                                    <td style={{ fontSize: "0.78rem", color: "#666", padding: "10px 0" }}>{fechaStr}</td>
                                                    <td style={{ fontSize: "0.78rem", fontWeight: 500 }}>{iniciales}</td>
                                                    <td style={{ fontSize: "0.78rem", color: "#666" }}>{l.telefono || "—"}</td>
                                                    <td style={{ fontSize: "0.78rem", color: "#666" }}>{emailMask}</td>
                                                    <td>
                                                        <span style={{
                                                            display: "inline-block",
                                                            fontSize: "0.7rem",
                                                            fontWeight: 600,
                                                            padding: "3px 10px",
                                                            borderRadius: 12,
                                                            backgroundColor: `${estado.color}26`,
                                                            color: estado.color,
                                                        }}>
                                                            {estado.label}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {(datos.ultimosLeads || []).length === 0 && (
                                            <tr><td colSpan={5} className="text-center text-muted py-4">Sin leads registrados</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-5">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                <Icono.Lightbulb color="#666" /> Insights automáticos
                            </h6>
                            {insights.length === 0 ? (
                                <p className="text-muted" style={{ fontSize: "0.85rem" }}>Sin datos suficientes para generar insights.</p>
                            ) : insights.map((ins, i) => {
                                const iconos = [<Icono.TrendingUp />, <Icono.Target />, <Icono.BarChart />, <Icono.Calendar />];
                                return (
                                    <div key={i} className="d-flex gap-3 mb-3">
                                        <IconBubble color={VERDE} size={36}>{iconos[i % iconos.length]}</IconBubble>
                                        <div>
                                            <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#222", marginBottom: 2 }}>
                                                {ins.titulo}
                                            </div>
                                            <div style={{ fontSize: "0.78rem", color: "#666", lineHeight: 1.5 }}>
                                                {ins.detalle}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <button className="btn btn-sm w-100 mt-2" style={{
                                fontSize: "0.78rem",
                                border: "1px solid #ddd",
                                color: "#555",
                                fontWeight: 500,
                            }}>
                                Ver todas las recomendaciones →
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
